// Power Dial modal — concurrency 1-5, redial attempts 1-3.
//
// Spec §3 (PowerDialModal): segmented controls + Start Dialing CIVIX gradient.
// Spec §10: defaults persist per-agent (localStorage).
// Spec §AC #1-10: validates inputs server-side; client-side here is a UX
// safety net only.

import React, { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, ShieldCheck, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePowerDial, usePowerDialDefaults } from '../state/powerDial.js';
import { powerdialApi } from '../lib/powerdial.js';
import { isReady as isSoftphoneReady, placeCall as softphonePlaceCall, initVoice } from '../lib/twilioVoice.js';
import { start as startProgressTone, stop as stopProgressTone } from '../lib/progressTone.js';
import useMicrophoneAuth from '../hooks/useMicrophoneAuth.js';
import MicrophonePreflight from './dialer/MicrophonePreflight.jsx';
import { apiGet, apiPost } from '../lib/api.js';
import { CONCURRENCY_CHOICES, REDIAL_CHOICES, SOFTPHONE_NOT_READY_MSG, MIC_DENIED_MSG, formatPhone } from './powerdial/powerDialConstants.js';
import ConcurrencySelector from './powerdial/ConcurrencySelector.jsx';
import RedialSelector from './powerdial/RedialSelector.jsx';
import { useDialGuards } from '../hooks/useDialGuards.jsx';
import { RESUMABLE_QUERY_KEY } from './powerdial/ResumeSessionBanner.jsx';
import SmartCallerIdSection from './powerdial/SmartCallerIdSection.jsx';
import ActiveSessionBanner, { WarnGateAlert, ModalErrorAlert } from './powerdial/ModalStatusBanners.jsx';

export { formatPhone };

// Prompt for microphone permission inside the user's click gesture.
async function ensureMicPermission() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { ok: true, unknown: true };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    try { stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.name || 'NotAllowedError' };
  }
}

export default function PowerDialModal() {
  const qc = useQueryClient();
  const { guardLead, guardModal } = useDialGuards();
  const { modalOpen, modalLeadIds, closeModal, setSession } = usePowerDial();
  const {
    concurrency,
    redialAttempts,
    smartLocalId: defaultSmartLocalId = true,
    setDefaults,
  } = usePowerDialDefaults();
  const micAuth = useMicrophoneAuth();
  const [c, setC] = useState(concurrency);
  const [r, setR] = useState(redialAttempts);
  const [smartLocalId, setSmartLocalId] = useState(defaultSmartLocalId);
  const [callerIdData, setCallerIdData] = useState({ numbers: [], defaultCallerId: '+16615249257', totalOwned: 0 });
  const [selectedCallerId, setSelectedCallerId] = useState('');
  const [matchPreview, setMatchPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [warnGate, setWarnGate] = useState(null);
  const [existingSessionId, setExistingSessionId] = useState(null);
  const [cancellingExisting, setCancellingExisting] = useState(false);

  // Sync local control state when defaults change (e.g. after first save)
  React.useEffect(() => {
    if (modalOpen) {
      const maxPossibleConcurrency = Math.max(1, modalLeadIds.length);
      const initialConcurrency = Math.min(concurrency || 1, maxPossibleConcurrency);
      setC(initialConcurrency);
      setR(redialAttempts || 1);
      setSmartLocalId(defaultSmartLocalId);
      setError(null);
      setWarnGate(null);
      setExistingSessionId(null);
      setCancellingExisting(false);

      // Pre-check for any active or paused session
      powerdialApi.getActive().then((res) => {
        if (res?.session?._id) {
          setSession(res.session);
          setExistingSessionId(res.session._id);
        }
      }).catch(() => {});

      setMatchPreview(null);
      powerdialApi.previewCallerIds(modalLeadIds)
        .then((res) => setMatchPreview(res?.data || res || null))
        .catch(() => {});

      powerdialApi.getCallerIds()
        .then((res) => {
          if (res?.data) {
            setCallerIdData(res.data);
            setSelectedCallerId(res.data.defaultCallerId || '');
          }
        })
        .catch(() => {});

      if (!isSoftphoneReady()) {
        apiPost('/phone/voice-token', {})
          .then((res) => {
            const token = res?.data?.token;
            if (token) return initVoice(token);
          })
          .catch(() => {});
      }
    }
  }, [modalOpen, concurrency, redialAttempts, defaultSmartLocalId, modalLeadIds.length]);

  // Escape key closes (a11y) — only when the modal is open.
  React.useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) closeModal?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalOpen, busy, closeModal]);

  const effectiveLeadCount = modalLeadIds.length;

  const estDuration = useMemo(() => {
    if (!effectiveLeadCount) return '—';
    const sets = Math.ceil(effectiveLeadCount / c);
    const perSet = 60; // rough average set duration in seconds, with retries
    const totalSec = sets * perSet * Math.max(1, r * 0.5);
    if (totalSec < 60) return `~${Math.round(totalSec)}s`;
    if (totalSec < 3600) return `~${Math.round(totalSec / 60)}m`;
    return `~${(totalSec / 3600).toFixed(1)}h`;
  }, [effectiveLeadCount, c, r]);

  if (!modalOpen) return null;

  const submit = async (allowWarn = false) => {
    if (!CONCURRENCY_CHOICES.includes(c)) {
      setError('Concurrency must be 1-5');
      return;
    }
    if (![1, 2, 3].includes(r)) {
      setError('Redial attempts must be 1-3');
      return;
    }
    const effectiveLeadIds = modalLeadIds;
    if (!effectiveLeadIds.length) {
      setError('No leads selected');
      return;
    }
    setBusy(true);
    setError(null);
    setExistingSessionId(null);
    let createdSessionId = null;

    // Single-lead kickoff: quiet-hours intercept (multi-lead sets are held/skipped by the engine).
    if (effectiveLeadIds.length === 1 && !(await guardLead(effectiveLeadIds[0]))) {
      setBusy(false);
      return;
    }

    // Check / initialize softphone if available
    let softphoneReady = isSoftphoneReady();
    if (!softphoneReady) {
      try {
        const tokenRes = await apiPost('/phone/voice-token', {});
        const token = tokenRes?.data?.token;
        if (token) {
          await initVoice(token);
          softphoneReady = isSoftphoneReady();
        }
      } catch (_) {}
    }

    if (c > 1 && softphoneReady) {
      try {
        const perm = await ensureMicPermission();
        if (perm && !perm.ok) {
          setError(MIC_DENIED_MSG);
          setBusy(false);
          return;
        }
      } catch (_) {}
    }

    try {
      // Persist defaults so next session pre-fills with this choice
      setDefaults({ concurrency: c, redialAttempts: r, smartLocalId });

      // 1. Create session
      const created = await powerdialApi.create({
        leadIds: effectiveLeadIds,
        concurrency: c,
        redialAttempts: r,
        smartLocalId,
        outboundCallerId: selectedCallerId || callerIdData.defaultCallerId || undefined,
        allowWarn,
        resumedFrom: usePowerDial.getState().resumeFromSessionId || undefined,
      });
      createdSessionId = created?.session?._id;
      usePowerDial.getState().setResumeFrom(null);
      qc.invalidateQueries({ queryKey: RESUMABLE_QUERY_KEY });

      // 2. Start the session on the backend
      const started = await powerdialApi.start(created.session._id, softphoneReady);

      // 3. Multi-line: join the per-session conference room
      if (c > 1 && started?.session?._id) {
        startProgressTone();
        try {
          const call = softphonePlaceCall('conference', { pdSession: started.session._id });
          if (call && typeof call.then === 'function') {
            await call;
          }
        } catch (err) {
          stopProgressTone();
          if (createdSessionId) {
            await powerdialApi.cancel(createdSessionId).catch(() => {});
          }
          setError(`Could not join dial room: ${err?.message || 'phone error'} — check mic permission and try again`);
          setBusy(false);
          return;
        }
      } else {
        try { startProgressTone(); } catch (_) {}
      }

      setSession(started?.session || created?.session);
      qc.invalidateQueries({ queryKey: ['call-history'] });
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
      qc.prefetchQuery({
        queryKey: ['call-history', { page: 1, limit: 50 }],
        queryFn: () => apiGet('/phone/calls', { page: 1, limit: 50 }),
      });
      toast.success(`Power Dial started — ${effectiveLeadIds.length} leads queued`);
      closeModal();
    } catch (err) {
      // If we already closed the modal but hit an error, re-open or show toast
      const data = err.response?.data;
      if (data?.code === 'number_caution' && data?.gate) {
        // Re-open the modal with the caution gate alert
        // (modal was already closed so we can't setWarnGate — show toast instead)
        toast.error('Number flagged Caution. Please try again and confirm.', { duration: 5000 });
      } else if (data?.code === 'session_exists' || err?.response?.status === 409 || err?.message?.includes('already have an active')) {
        const sid = data?.activeSessionId || true;
        setExistingSessionId(sid);
        powerdialApi.getActive().then((res) => {
          if (res?.session) {
            setSession(res.session);
            setExistingSessionId(res.session._id);
          }
        }).catch(() => {});
      } else {
        // Only show error in modal if we haven't already closed it
        if (modalOpen) {
          setError(data?.error || err.message || 'Failed to start Power Dial');
        } else {
          toast.error(data?.error || err.message || 'Failed to start Power Dial');
        }
        // Cancel session if created
        if (createdSessionId) {
          await powerdialApi.cancel(createdSessionId).catch(() => {});
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCancelExistingAndRestart = async () => {
    setCancellingExisting(true);
    try {
      if (existingSessionId && typeof existingSessionId === 'string') {
        await powerdialApi.cancel(existingSessionId);
      } else {
        const active = await powerdialApi.getActive();
        if (active?.session?._id) {
          await powerdialApi.cancel(active.session._id);
        }
      }
      setSession(null);
      setExistingSessionId(null);
      setError(null);
      toast.success('Previous session cancelled. You can now start dialing.');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to cancel session');
    } finally {
      setCancellingExisting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-999 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="powerdial-modal-title"
    >
      {guardModal}
      <div className="w-full max-w-lg bg-[#0F0E17] border border-white/10 rounded-2xl shadow-2xl p-6 text-white max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
          <div>
            <h2 id="powerdial-modal-title" className="text-lg font-bold">
              Power Dial
            </h2>
            <p className="text-xs text-white/50">
              {effectiveLeadCount} {effectiveLeadCount === 1 ? 'lead' : 'leads'} selected
            </p>
          </div>
          <button
            onClick={closeModal}
            className="text-white/40 hover:text-white transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Parallel Lines (Concurrency) */}
        <ConcurrencySelector
          concurrency={c}
          setConcurrency={setC}
          effectiveLeadCount={effectiveLeadCount}
        />

        {/* Redial Attempts */}
        <RedialSelector
          redialAttempts={r}
          setRedialAttempts={setR}
        />

        {/* Outbound Caller ID & Smart Local ID */}
        <SmartCallerIdSection
          smartLocalId={smartLocalId}
          setSmartLocalId={setSmartLocalId}
          callerIdData={callerIdData}
          selectedCallerId={selectedCallerId}
          setSelectedCallerId={setSelectedCallerId}
          matchPreview={matchPreview}
        />

        <div className="text-xs text-white/50 mb-4 flex justify-between">
          <span>Estimated duration:</span>
          <span className="text-white/80 font-mono">{estDuration}</span>
        </div>

        <WarnGateAlert warnGate={warnGate} onSubmit={submit} />

        {/* Microphone Pre-Flight Authorization & Device Status */}
        <div className="mb-4">
          <MicrophonePreflight auth={micAuth} compact={micAuth.isReady} />
        </div>

        <ModalErrorAlert
          error={error}
          onSwitchToOneLine={() => { setC(1); setError(null); }}
          onClearError={() => setError(null)}
        />

        <ActiveSessionBanner
          existingSessionId={existingSessionId}
          cancellingExisting={cancellingExisting}
          busy={busy}
          onCancelExistingAndRestart={handleCancelExistingAndRestart}
          onCloseModal={closeModal}
          toast={toast}
        />

        <div className="flex items-center gap-3 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={closeModal}
            disabled={busy}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white border border-white/10 hover:border-white/30 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !modalLeadIds.length || !!warnGate || micAuth.permission === 'denied'}
            onClick={() => submit(false)}
            className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white
                       bg-linear-to-r from-[#7C3AED] to-[#E040FB]
                       hover:shadow-lg hover:shadow-[#7C3AED]/40
                       disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            title={micAuth.permission === 'denied' ? 'Microphone access is blocked in browser settings' : undefined}
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Start Dialing
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
